<div
  class="scrollbar-dark fixed left-0 right-0 top-0 bottom-0 flex items-center justify-center pointer-events-none"
  data-testid="root-container"
>
  <div
    #window
    [ngStyle]="windowStyle()"
    class="absolute bg-neutral-800 text-neutral-400 rounded-2xl overflow-hidden pointer-events-auto"
    data-testid="window"
  >
    <ng-container *ngTemplateOutlet="header"></ng-container>
    <ng-container *ngTemplateOutlet="content"></ng-container>
  </div>
</div>

@if (isInitialLoadingWindowMode()) {
  <div
    class="scrollbar-dark fixed left-0 right-0 top-0 bottom-0 flex items-center justify-center pointer-events-none"
  >
    <div
      class="absolute p-4 cursor-progress text-lg text-neutral-900 rounded-xl w-[300px] bg-neutral-200 shadow-lg"
    >
      <flx-ai-pending-message
        [status]="'Loading agent ...'"
        [message]="'Getting context information'"
      />
    </div>
  </div>
}

<ng-template #header>
  <div
    [flxDrag]="DragType.FREE"
    [flxDragTarget]="window"
    [ngClass]="{
      hidden: isInitialLoadingWindowMode(),
    }"
    (flxDragInit)="onDragEnd($event)"
    (flxDragEnd)="onDragEnd($event)"
    (flxDragMove)="onDragMove($event)"
    class="w-full flex bg-neutral-900 py-2 px-6 cursor-move"
    data-testid="header"
  >
    <div class="flex basis-1/3 items-center justify-start gap-2">
      <flx-icon
        name="stars-flowx-yellow"
        data-testid="header-icon"
      />
      <span
        class="font-bold capitalize text-[10px] leading-[16px] select-none"
        data-testid="header-title"
      >
        {{ title() }}
      </span>
    </div>
    <div class="flex basis-1/3 items-center justify-center">
      <flx-icon
        name="grip-lines"
        data-testid="header-grip-icon"
      />
    </div>
    <div class="flex basis-1/3 items-center justify-end">
      <flx-icon
        (click)="onMinimizeClick()"
        name="circle-minus"
        class="cursor-pointer"
        data-testid="minimize-button"
      />
    </div>
  </div>
</ng-template>

<ng-template #content>
  <div
    [ngClass]="{
      'max-w-[50vw] min-w-[700px]': isCommandPaletteWindowMode(),
      'w-[500px]': isAiChatWindowMode(),
      'hidden': isInitialLoadingWindowMode(),
    }"
    class="border-y-[0.5px] border-neutral-700"
    data-testid="content"
  >
    @if (hasBreadcrumbs()) {
      <div
        class="flex flex-col border-b-[0.5px] border-b-neutral-700 py-3 px-6"
        data-testid="breadcrumbs-container"
      >
        <flx-ai-breadcrumb
          [breadcrumbs]="breadcrumbs()"
          [disabled]="isBreadcrumbsBackDisabled()"
          [class.mb-4]="isCommandPaletteWindowMode()"
          (back)="onBreadcrumbsBack()"
          data-testid="breadcrumbs"
        />
      </div>
    }

    @if (showCommandPaletteTypeArea()) {
      <div
        class="flex flex-col border-b-[0.5px] border-b-neutral-700 p-6"
        data-testid="command-palette-type-area-container"
      >
        <flx-ai-type-area
          [hint]="typeAreaPlaceholder()"
          (currentValue)="onTypeAreaValue($event)"
          tabindex="0"
          data-testid="command-palette-type-area"
        />
      </div>
    }

    @if (showContextDocuments()) {
      <div
        class="relative bg-black/10 p-2 text-white flex justify-between items-center text-xs border-b-[0.5px] border-b-neutral-700"
        data-testid="context-documents-bar"
      >
        <div class="flex gap-2 items-center">
          <span class="text-neutral-300">{{ contextDocumentsStatus() }}:</span>
          <span class="font-bold">{{ contextDocumentsName() }}</span>
        </div>

        @if (selectedDocument()) {
          <button
            [disabled]="isCloseContextDocumentDisabled()"
            (click)="onContextDocumentCloseClick()"
            class="group cursor-pointer disabled:cursor-not-allowed"
            data-testid="context-document-close-button"
          >
            <flx-icon
              name="cross"
              class="group-disabled:text-neutral-400"
            />
          </button>
        }

        @if (isUploadingDocuments()) {
          <p-progressBar
            class="absolute left-0 right-0 h-[1px] bottom-0 overflow-hidden"
            mode="indeterminate"
            data-testid="context-document-upload-progress"
          />
        }
      </div>
    }

    <div class="mb-[0.5px] border-b-[0.5px] border-b-neutral-700">
      @if (isCommandPaletteWindowMode() && (commands()?.length || actions()?.length)) {
        @if (isSearchingDocuments()) {
          <p-progressBar
            class="absolute left-0 right-0 h-[2px] top-[30px] overflow-hidden"
            mode="indeterminate"
          />
        }
        <flx-ai-command-palette
          [commands]="commands()"
          [actions]="actions()"
          [highlight]="highlightedCommand()"
          tabIndex="1"
          data-testid="command-palette"
        />
        <div class="max-h-96 flex flex-row overflow-hidden w-full max-w-full">
          <div class="flex flex-col max-h-96 overflow-y-auto w-full h-full">
            @if (searchResult()?.length) {
              <div class="flex flex-row w-full">
                <div
                  class="flex items-center justify-between px-6 pt-2 pb-1 text-xs/[18px] font-bold border-b-[0.5px] border-b-neutral-700 w-full"
                  data-testid="search-result-header"
                >
                  <span class="h-[24px]">Best Matches:</span>
                </div>
              </div>
            }
            <div
              class="flex flex-col w-full h-full overflow-y-auto border-r-[0.5px] border-r-neutral-700"
            >
              @for (item of searchResult(); track item) {
                <div
                  (click)="onSearchResultClick(item)"
                  class="px-6 py-2 flex flex-row gap-2 border-b-[0.5px] border-b-neutral-700 hover:bg-neutral-600 cursor-pointer"
                  tabindex="0"
                >
                  <i [class]="getIconByType(item.object_type)"></i>
                  <div class="flex flex-col w-full">
                    <div class="flex flex-row gap-1 item-center align-center justify-between">
                      <span class="text-sm font-bold text-slate-100">{{ item.name }}</span>
                      <flx-confidence-indicator
                        [confidenceLevel]="item.distance"
                      ></flx-confidence-indicator>
                    </div>
                    <div class="text-xs font-semi-bold font-mono text-slate-300">
                      {{ item.process_name + ' / ' + item.name }}
                    </div>
                    <div
                      [innerHTML]="item.description | flxAiMarkdown | async"
                      class="docs-result overflow-hidden text-ellipsis rounded-xs text-xs min-h-6 max-h-8 text-slate-300 whitespace-normal break-normal w-full"
                    ></div>
                  </div>
                </div>
              }
            </div>
          </div>
          @if (currentResultItem()) {
            <div class="flex flex-col w-1/2">
              <div
                class="flex flex-row item-center justify-between pt-2 pb-1 px-4 border-b-[0.5px] border-b-neutral-700"
              >
                <div class="text-xs text-neutral-300 gap-2 flex flex-row item-center">
                  <i [class]="getIconByType(currentResultItem().object_type)"></i>
                  <span
                    [pTooltip]="currentResultItem().name"
                    class="text-xs/[18px] font-bold w-[128px] text-ellipsis overflow-hidden"
                    >{{ currentResultItem().name }}</span
                  >
                </div>
                <div class="flex flex-row gap-2">
                  <div
                    (click)="onCloseCurrentResultItem()"
                    class="cursor-pointer"
                    class="hover:text-neutral-300"
                    data-testid="close-current-result-item-button"
                    pTooltip="Close"
                    tooltipPosition="top"
                    tabindex="0"
                  >
                    <i class="ph ph-x text-neutral-300"></i>
                  </div>
                  <div
                    (click)="goToObject(currentResultItem())"
                    class="cursor-pointer"
                    class="hover:text-neutral-300"
                    data-testid="close-current-result-item-button"
                    pTooltip="GoTo Resource"
                    tooltipPosition="top"
                    tabindex="1"
                  >
                    <i class="ph ph-link text-neutral-300"></i>
                  </div>
                </div>
              </div>
              <div
                [innerHTML]="currentResultItem().description | flxAiMarkdown | async"
                class="docs-result overflow-auto max-h-96 p-2 text-ellipsis rounded-xs text-xs whitespace-normal break-normal w-full"
              ></div>
            </div>
          }
        </div>
      }

      @if (messages().length) {
        <flx-ai-chat-history
          [messages]="messages()"
          data-testid="chat-history"
        />
      }
    </div>

    @if (isCommandPaletteWindowMode()) {
      <flx-ai-shortcuts
        [leftShortcuts]="commands()?.length ? [Shortcuts.SELECT] : []"
        [centerShortcuts]="commands()?.length ? [Shortcuts.OPEN] : []"
        [rightShortcuts]="[Shortcuts.EXIT]"
        data-testid="command-palette-shortcuts"
      />
    }

    @if (isAiChatWindowMode()) {
      @if (showUploadedImages()) {
        @for (image of images(); track image) {
          <div class="px-6 pt-2">
            <div
              class="flex flex-col rounded-lg p-2 bg-black/30 gap-1 w-[112px] h-[142px] overflow-hidden"
              data-testid="uploaded-image"
            >
              <div class="relative overflow-hidden rounded-lg">
                <p-image
                  [src]="image.src$ | async"
                  [alt]="image.name"
                  class="w-[96px] h-[108px] object-cover"
                  width="96px"
                  preview="true"
                  height="108px"
                  imageClass="min-w-[96px] w-auto min-h-[108px] h-auto object-contain"
                />
                <button
                  (click)="deleteFileFromContext(image.id)"
                  class="absolute right-1 bottom-1 h-5 w-5 flex justify-center items-center rounded-[6px] bg-black cursor-pointer"
                  data-testid="delete-uploaded-image-button"
                >
                  <flx-icon
                    name="trash"
                    class="w-3 h-3 text-red-500"
                  />
                </button>
              </div>
              <div
                class="h-4 text-[10px] leading-[8px] text-white truncate grow-0"
                data-testid="uploaded-image-name"
              >
                {{ image.name }}
              </div>
            </div>
          </div>
        }
      }

      @if (showUploadedBpmnFiles()) {
        @for (bpmn of bpmnFiles(); track bpmn) {
          <div class="px-6 pt-2">
            <div
              class="flex flex-col rounded-lg p-2 bg-black/30 gap-1 w-[112px] h-[142px] overflow-hidden"
              data-testid="uploaded-bpmn"
            >
              <div class="relative overflow-hidden rounded-lg">
                <p-image
                  [alt]="bpmn.name"
                  class="w-[96px] h-[108px]"
                  src="./assets/bpmn_thumb_full.svg"
                  width="96px"
                  height="108px"
                  imageClass="min-w-[96px] w-auto min-h-[108px] h-auto object-cover"
                  data-testid="uploaded-bpmn-preview"
                />
                <button
                  (click)="deleteFileFromContext(bpmn.id)"
                  class="absolute right-1 bottom-1 h-5 w-5 flex justify-center items-center rounded-[6px] bg-black cursor-pointer"
                  data-testid="delete-uploaded-bpmn-button"
                >
                  <flx-icon
                    name="trash"
                    class="w-3 h-3 text-red-500"
                  />
                </button>
              </div>
              <div
                class="h-4 text-[10px] leading-[8px] text-white truncate grow-0"
                data-testid="uploaded-bpmn-name"
              >
                {{ bpmn.name }}
              </div>
            </div>
          </div>
        }
      }

      @if (showChatTypeArea()) {
        <div
          class="flex flex-col p-6"
          data-testid="chat-type-area-container"
        >
          <flx-ai-type-area
            hint="Ask me to explain or do something..."
            tabIndex="2"
            data-testid="chat-type-area"
          />
        </div>
      }
    }
  </div>
</ng-template>
